/*
  Nova Aura 2026-10-28 — nombres de producto siempre en una sola línea.
  Marca cada nombre de producto con [data-nova-fit] (estilos en nova-title-fit.css) y, si no cabe
  en el ancho disponible, reduce su tamaño de letra lo justo para que entre. No cambia ningún texto.
  Se recalcula al cambiar el ancho (móvil/ordenador, giro de pantalla) y cuando el tema vuelve a
  pintar zonas (carrito lateral, barra fija, ventana de compra rápida).
  Las cuadrículas de tarjetas (colecciones, inicio, búsqueda, carruseles) NO pasan por aquí: allí
  todos los nombres van al mismo tamaño y se cortan con «…» solo con CSS (nova-title-fit.css).
*/
(() => {
  // Nombres dentro de cuadrículas de tarjetas: se excluyen del ajuste de tamaño.
  const GRID_TITLE = 'product-card a[ref="productTitleLink"] .text-block > *, [data-resource-type="product"] .resource-card__title';
  const SELECTORS = [
    // Bloques de título de producto fuera de las tarjetas (p. ej. producto destacado)
    'a[ref="productTitleLink"] .text-block > *',
    // Ficha de producto (título principal)
    '.product-details .text-block > h1',
    // Bloque "historia" de la ficha cuando muestra el nombre del producto
    '.nova-pstory__title[data-nova-fit-title]',
    // Barra fija de compra
    '.sticky-add-to-cart__title',
    // Carrito (página y lateral) y sugerencias del carrito
    '.cart-items__title',
    '.nova-upsell__title',
    // Compra rápida, puntos de producto, buscador, galerías con enlace a producto
    '.view-product-title a',
    '.hotspot-dialog__product-title',
    '.nova-review-gallery__card a',
    '.nova-videos__product',
    '.pickup-location__h4',
  ].join(',');

  const ATTR = 'data-nova-fit';
  // Nunca por debajo de este tamaño (px); si aun así no cabe, el nombre completo se desliza
  // lentamente en su línea (ver nova-title-fit.css).
  const MIN_PX = 11;
  const SPEED = 28; // px por segundo del desplazamiento
  const seen = new WeakSet();
  const lastWidth = new WeakMap();
  let queued = new Set();
  let frame = 0;

  // Ajusta una lista de nombres a un tamaño común (aquí se usa de uno en uno).
  function fitTogether(list) {
    // Ocultos (cajón cerrado, lista solo de ordenador en móvil…): se dejan hasta que tengan ancho.
    const els = list.filter((el) => el.isConnected && el.clientWidth > 0);
    if (!els.length) return;
    els.forEach((el) => {
      el.removeAttribute('data-nova-fit-marquee');
      el.style.removeProperty('font-size');
      el.style.removeProperty('line-height');
    });
    const info = els.map((el) => {
      const cs = getComputedStyle(el);
      const base = parseFloat(cs.fontSize) || 16;
      const avail = el.clientWidth;
      const natural = el.scrollWidth;
      const ideal = natural > avail + 0.5 ? base * ((avail - 1) / natural) : base;
      return {
        el,
        base,
        // Se conserva la altura de la línea original para que precios y botones sigan
        // alineados aunque el nombre se haya reducido.
        lineHeight: cs.lineHeight.endsWith('px') ? cs.lineHeight : el.offsetHeight + 'px',
        size: Math.max(MIN_PX, Math.floor(ideal * 4) / 4),
      };
    });
    let size = Math.min(...info.map((i) => i.size));
    const apply = () => info.forEach((i) => {
      if (size < i.base) {
        i.el.style.setProperty('line-height', i.lineHeight, 'important');
        i.el.style.setProperty('font-size', size + 'px', 'important');
        i.el.setAttribute('data-nova-fit-scaled', '');
      } else {
        i.el.removeAttribute('data-nova-fit-scaled');
      }
    });
    apply();
    // Ajuste fino por redondeos de la fuente
    const overflows = (i) => i.el.scrollWidth > i.el.clientWidth + 0.5;
    for (let n = 0; n < 6 && size > MIN_PX && info.some((i) => size < i.base && overflows(i)); n++) {
      size = Math.max(MIN_PX, size - 0.25);
      apply();
    }
    // Lo que no cabe ni al tamaño mínimo se desliza en su línea
    const rests = info.map((i) => i.el.scrollWidth - i.el.clientWidth);
    info.forEach((i, k) => {
      if (rests[k] > 0.5) {
        const shift = Math.ceil(rests[k]) + 4;
        // Pausa al principio y al final (≈35 % del ciclo) + recorrido a velocidad constante
        const dur = Math.max(5, (shift / SPEED) / 0.65);
        i.el.style.setProperty('--nova-fit-shift', -shift + 'px');
        i.el.style.setProperty('--nova-fit-dur', dur.toFixed(2) + 's');
        i.el.setAttribute('data-nova-fit-marquee', '');
      }
    });
    // Ancho tras el ajuste (puede encoger si el elemento se adapta al texto): así el
    // ResizeObserver no vuelve a lanzar el cálculo por un cambio que hemos provocado nosotros.
    info.forEach((i) => lastWidth.set(i.el, i.el.clientWidth));
  }

  const ro = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        for (const e of entries) {
          const el = e.target;
          const w = el.clientWidth;
          // Si el ancho no ha cambiado, el cambio viene de nuestro propio ajuste: no repetir.
          if (lastWidth.get(el) === w) continue;
          queue(el);
        }
      })
    : null;

  function queue(el) {
    queued.add(el);
    if (!frame) frame = requestAnimationFrame(flush);
  }

  function flush() {
    frame = 0;
    const list = queued;
    queued = new Set();
    list.forEach((el) => fitTogether([el]));
  }

  function scan(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const found = [];
    if (scope.matches && scope.matches(SELECTORS)) found.push(scope);
    scope.querySelectorAll(SELECTORS).forEach((el) => found.push(el));
    for (const el of found) {
      if (el.closest('.visually-hidden') || el.matches(GRID_TITLE)) continue;
      if (!seen.has(el)) {
        seen.add(el);
        el.setAttribute(ATTR, '');
        if (ro) ro.observe(el);
      }
      queue(el);
    }
  }

  function refitAll() {
    document.querySelectorAll('[' + ATTR + ']').forEach(queue);
  }

  // Nuevos elementos o textos que cambian (carrito, barra fija al cambiar de variante, etc.)
  const mo = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === 'characterData') {
        const host = r.target.parentElement && r.target.parentElement.closest('[' + ATTR + ']');
        if (host) queue(host);
        continue;
      }
      r.addedNodes.forEach((n) => {
        if (n.nodeType === 1) scan(n);
        else if (n.nodeType === 3 && r.target.closest) {
          const host = r.target.closest('[' + ATTR + ']');
          if (host) queue(host);
        }
      });
    }
  });

  function start() {
    scan(document);
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refitAll);
    window.addEventListener('load', refitAll);
    window.addEventListener('pageshow', refitAll);
    // Solo cuando cambia el ancho de la ventana (en móvil, la barra del navegador cambia el alto
    // al hacer scroll y no debe reiniciar nada). Los cajones y ventanas que se abren los detecta
    // el ResizeObserver, porque el nombre pasa de ancho 0 a su ancho real.
    let vw = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth === vw) return;
      vw = window.innerWidth;
      refitAll();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
